var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { AccessLoadingSpinner } from './AccessLoadingSpinner';
import { Button } from 'antd';
import { createAsyncSelectors } from '@lib/selectors';
import { DataAccessSearch } from './DataAccessSearch';
import { getColumns } from './AccessAuditColumns';
import { getQueryString } from '@sonar-software/sonar-http';
import { parseQueryString } from '@sonar-software/sonar-ts-lib';
import { SelectionTable } from '@ui/tables';
import { SonarCard } from '@ui/general';
import { coreActions, SchemaFormat } from '@sonar-software/sonar-core';
import { COPY_SPECIFIC, FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { SonarV3SortDirection } from '@sonar-software/sonar-ts-types';
import { Else, If, Then } from 'react-if';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TableModifier } from '@ui/table-modifier';
import { translateFilterAndOrderingToQueryParams, translateFilterQueryParamsToDynamicQueryLanguage, translateOrderQueryParamsToDynamicQueryLanguage } from '@ui/table-modifier/@arch/lib';
import { useAction, useQueryParams, useSelectionTableWithInversion, useSonarSelector } from '@hooks';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
const filters = [
    {
        filterFormat: SchemaFormat.Text,
        filterGroup: COPY_SPECIFIC.DATA_ACCESS,
        filterId: 'AccessName',
        filterLabel: COPY_SPECIFIC.ACCESS_NAME
    }, {
        filterFormat: SchemaFormat.Enum,
        filterGroup: COPY_SPECIFIC.DATA_ACCESS,
        filterId: 'AccessType',
        filterLabel: COPY_SPECIFIC.ACCESS_TYPE
    }, {
        filterFormat: SchemaFormat.Enum,
        filterGroup: COPY_SPECIFIC.DATA_ACCESS,
        filterId: 'AccessLevel',
        filterLabel: COPY_SPECIFIC.OBJECT_ACCESS
    }
];
const sortOptions = [
    {
        direction: SonarV3SortDirection.Ascending,
        key: 'AccessName',
        label: COPY_SPECIFIC.ACCESS_NAME
    },
    {
        direction: SonarV3SortDirection.Ascending,
        key: 'AccessType',
        label: COPY_SPECIFIC.ACCESS_TYPE
    }
];
const columns = getColumns();
const getRowKey = (accessObject) => accessObject.accessId;
const convertToDql = (params) => {
    const { objectName } = params, rest = __rest(params, ["objectName"]);
    return {
        filters: translateFilterQueryParamsToDynamicQueryLanguage(rest),
        ordering: translateOrderQueryParamsToDynamicQueryLanguage(rest)
    };
};
export const accessAuditDataSelectors = createAsyncSelectors((state) => state.core.dataSecurity.dataAccess.accessAudit.get);
export const AccessAudit = () => {
    const [sorting, setSorting] = useState(false);
    const { id } = useParams();
    const lastUsedParams = useRef();
    const navigate = useNavigate();
    const location = useLocation();
    const getAudit = useAction(coreActions.requestGetCoreDataSecurityAccessAuditById);
    const data = useSonarSelector(accessAuditDataSelectors.data);
    const isRequested = useSonarSelector(accessAuditDataSelectors.isRequested);
    const parsedQuery = useMemo(() => parseQueryString(location.search), [location.search]);
    const initialParams = useMemo(() => (Object.assign({ filters: '', ordering: '' }, parsedQuery)), [parsedQuery]);
    const updateUrl = useCallback((queryObject) => {
        navigate(`${FULL_ROUTES.DATA_SECURITY_DATA_ACCESS_ACCESS_AUDIT}/${id}?${getQueryString(queryObject)}`, { replace: true });
    }, [id, navigate]);
    const callApi = useCallback((params) => {
        if (id) {
            lastUsedParams.current = params;
            getAudit(Object.assign({ salesforceId: id }, params));
        }
    }, [getAudit, id]);
    const { queryParams, updateParams } = useQueryParams({
        defaultParams: initialParams,
        onUpdate: (_, __, combined) => {
            var _a, _b;
            const urlParams = {
                filters: (_a = combined.filters) !== null && _a !== void 0 ? _a : '',
                objectName: combined.objectName,
                ordering: (_b = combined.ordering) !== null && _b !== void 0 ? _b : ''
            };
            updateUrl(urlParams);
        }
    });
    useEffect(() => {
        if (parsedQuery.objectName !== queryParams.objectName) {
            updateParams({ objectName: parsedQuery.objectName });
        }
    }, [parsedQuery, queryParams, updateParams]);
    const sortedData = useMemo(() => {
        if (!data) {
            return [];
        }
        setSorting(true);
        const params = translateFilterAndOrderingToQueryParams([], sortOptions, queryParams.filters, queryParams.ordering);
        const sorted = [...data].sort((a, b) => {
            if (params.sortBy === 'AccessType') {
                return params.sortDirection === SonarV3SortDirection.Ascending ?
                    a.accessType.localeCompare(b.accessType) :
                    b.accessType.localeCompare(a.accessType);
            }
            return params.sortDirection === SonarV3SortDirection.Ascending ||
                params.sortDirection === undefined ?
                a.accessName.toLowerCase().localeCompare(b.accessName.toLowerCase()) :
                b.accessName.toLowerCase().localeCompare(a.accessName.toLowerCase());
        });
        setSorting(false);
        return sorted;
    }, [data, queryParams, setSorting]);
    useEffect(() => {
        const lastUsed = lastUsedParams.current;
        if (queryParams.filters === (lastUsed === null || lastUsed === void 0 ? void 0 : lastUsed.filters) &&
            queryParams.objectName === (lastUsed === null || lastUsed === void 0 ? void 0 : lastUsed.objectName)) {
            return;
        }
        callApi(queryParams);
    }, [callApi, queryParams]);
    const selectionData = useSelectionTableWithInversion({
        getRowKey,
        results: sortedData,
        totalCount: sortedData.length
    });
    const handleCompare = useCallback(() => {
        const params = {
            accessType: [],
            accessTypeId: []
        };
        selectionData.selectedKeys.forEach((key) => {
            const access = sortedData.find((item) => item.accessId === key);
            if (!access) {
                return;
            }
            params.accessType.push(access.accessType);
            params.accessTypeId.push(access.accessId);
        });
        const compareUrl = `${FULL_ROUTES.DATA_SECURITY_DATA_ACCESS_ACCESS_COMPARE}?${getQueryString(params)}`;
        navigate(compareUrl);
    }, [navigate, selectionData, sortedData]);
    return (React.createElement(SonarCard, { title: COPY_SPECIFIC.ACCESS_AUDIT },
        React.createElement(DataAccessSearch, { defaultValue: initialParams.objectName }),
        React.createElement(If, { condition: isRequested || sorting },
            React.createElement(Then, null,
                React.createElement(AccessLoadingSpinner, { loading: isRequested, sorting: sorting })),
            React.createElement(Else, null,
                React.createElement(TableModifier, { className: 'compact-action-bar', convertToDynamicQueryLanguage: convertToDql, deferFiltering: true, extra: React.createElement(Button, { onClick: () => callApi(queryParams) }, COPY_SPECIFIC.REFRESH_DATA), filterDisplayType: 'menu', filters: filters, filtersString: queryParams.filters, onChange: updateParams, orderString: queryParams.ordering, sortOptions: sortOptions }),
                React.createElement(SelectionTable, { className: 'access-audit-table', columns: columns, dataSource: sortedData, editLabel: COPY_SPECIFIC.COMPARE, hideSelectAll: true, onEditSelection: handleCompare, rowKey: getRowKey, selectionData: selectionData })))));
};
